import React, { useState } from "react";
import doors3dData from "../data/3d.json";

export default function Door3DViewer({ doorImage, categoryType, onToggle2D, colors, selectedDoor }) {
    const [explodeValue, setExplodeValue] = useState(45); // Separation distance in pixels
    const [perspectivePreset, setPerspectivePreset] = useState("isometric");
    const [activeLayer, setActiveLayer] = useState(0);

    // Find the 3D structural template from 3d.json
    const get3DModel = () => {
        const typeLower = (categoryType || "").toLowerCase();
        if (typeLower.includes("prelaminated")) {
            return doors3dData.find(d => d.name.toLowerCase().includes("prelaminated")) || doors3dData[0];
        }
        if (typeLower.includes("sunmica")) {
            return doors3dData.find(d => d.name.toLowerCase().includes("sunmica")) || doors3dData[1] || doors3dData[0];
        }
        if (typeLower.includes("teak")) {
            return doors3dData.find(d => d.name.toLowerCase().includes("teak")) || doors3dData[2] || doors3dData[0];
        }
        if (typeLower.includes("wpvc") || typeLower.includes("wpc")) {
            return doors3dData.find(d => d.name.toLowerCase().includes("wpvc")) || doors3dData[3] || doors3dData[0];
        }
        return doors3dData[0]; // fallback
    };

    const currentModel = get3DModel();
    const layers = currentModel ? currentModel.layers : [];

    const getPerspectiveStyle = () => {
        switch (perspectivePreset) {
            case "front":
                return { transform: "rotateX(0deg) rotateY(0deg) rotateZ(0deg)" };
            case "profile":
                return { transform: "rotateX(5deg) rotateY(-80deg) rotateZ(0deg)" };
            case "isometric":
            default:
                return { transform: "rotateX(60deg) rotateY(0deg) rotateZ(-45deg)" };
        }
    };

    return (
        <div className="modal-3d-dashboard-container">
            {/* Local CSS styles scoped specifically to modal-3d to prevent collisions */}
            <style dangerouslySetInnerHTML={{ __html: `
                .modal-3d-dashboard-container {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    background-color: #F7F7F7;
                    font-family: 'Poppins', sans-serif;
                    overflow: hidden;
                    box-sizing: border-box;
                }

                /* Header & Toggle Section */
                .modal-3d-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 24px;
                    background: #ffffff;
                    border-bottom: 1px solid #eef0eb;
                    flex-shrink: 0;
                    z-index: 10;
                }
                .modal-3d-brand {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .modal-3d-dot {
                    width: 8px;
                    height: 8px;
                    background-color: ${colors.gold};
                    border-radius: 50%;
                    box-shadow: 0 0 8px rgba(212, 175, 55, 0.5);
                }
                .modal-3d-brand-title {
                    font-size: 0.95rem;
                    font-weight: 800;
                    letter-spacing: 1px;
                    color: #1a1a1a;
                    margin: 0;
                }
                .modal-3d-brand-subtitle {
                    font-size: 0.65rem;
                    color: #7a715f;
                    margin-left: 5px;
                    background: rgba(212, 175, 55, 0.08);
                    padding: 1px 6px;
                    border-radius: 4px;
                    font-weight: bold;
                }

                /* Header Toggles */
                .modal-3d-header-toggles {
                    display: flex;
                    gap: 6px;
                }
                .modal-3d-header-toggle-btn {
                    border-radius: 20px;
                    padding: 5px 12px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .modal-3d-header-toggle-btn.secondary {
                    background: #fff;
                    color: #5D4037;
                    border: 1.5px solid ${colors.gold};
                }
                .modal-3d-header-toggle-btn.secondary:hover {
                    background: rgba(212, 175, 55, 0.05);
                }
                .modal-3d-header-toggle-btn.primary {
                    background: ${colors.gold};
                    color: #fff;
                    border: 1.5px solid ${colors.gold};
                    opacity: 0.95;
                }

                /* Main Body Grid */
                .modal-3d-body {
                    display: flex;
                    flex: 1;
                    overflow: hidden;
                    width: 100%;
                }

                /* Left Panel (Layer list tree) */
                .modal-3d-left-panel {
                    width: 280px;
                    background: rgba(255, 255, 255, 0.55);
                    border-right: 1px solid #eef0eb;
                    display: flex;
                    flex-direction: column;
                    padding: 20px;
                    overflow-y: auto;
                    flex-shrink: 0;
                }
                .modal-3d-panel-title {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: #2a2723;
                    margin: 0 0 4px 0;
                }
                .modal-3d-panel-desc {
                    font-size: 0.72rem;
                    color: #7a715f;
                    line-height: 1.4;
                    margin: 0 0 16px 0;
                }
                .modal-3d-layer-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .modal-3d-layer-btn {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: #ffffff;
                    border: 1px solid #e5e0d0;
                    border-radius: 8px;
                    padding: 10px 12px;
                    text-align: left;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    width: 100%;
                    position: relative;
                }
                .modal-3d-layer-btn:hover {
                    background: #fdfcf7;
                    border-color: #D4AF37;
                    transform: translateX(3px);
                }
                .modal-3d-layer-btn.active {
                    background: rgba(212, 175, 55, 0.06);
                    border-color: #D4AF37;
                }
                .modal-3d-layer-idx {
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #7a715f;
                    font-family: monospace;
                }
                .modal-3d-layer-btn.active .modal-3d-layer-idx {
                    color: #D4AF37;
                }
                .modal-3d-layer-info {
                    flex: 1;
                }
                .modal-3d-layer-name {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #2a2723;
                    margin: 0 0 2px 0;
                }
                .modal-3d-layer-mat {
                    font-size: 0.62rem;
                    color: #7a715f;
                    background: #f3ede0;
                    padding: 1px 5px;
                    border-radius: 3px;
                    display: inline-block;
                }
                .modal-3d-layer-btn.active .modal-3d-layer-glow {
                    position: absolute;
                    right: 0;
                    top: 0;
                    bottom: 0;
                    width: 3.5px;
                    background-color: #D4AF37;
                }

                /* Center Panel (3D Canvas) */
                .modal-3d-center-panel {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    background: #fafaf8;
                    overflow: hidden;
                }
                .modal-3d-canvas-wrapper {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    perspective: 1200px;
                    position: relative;
                    padding: 20px;
                    min-height: 250px;
                }
                .modal-3d-grid-bg {
                    position: absolute;
                    inset: 0;
                    background-size: 20px 20px;
                    background-image: linear-gradient(to right, rgba(212, 175, 55, 0.05) 1px, transparent 1px),
                                      linear-gradient(to bottom, rgba(212, 175, 55, 0.05) 1px, transparent 1px);
                    pointer-events: none;
                }
                .modal-3d-scene {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transform-style: preserve-3d;
                }
                .modal-door-3d-model {
                    position: relative;
                    width: 110px;
                    height: 230px;
                    transform-style: preserve-3d;
                    transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
                }
                .modal-door-3d-layer {
                    position: absolute;
                    inset: 0;
                    transform-style: preserve-3d;
                    transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.3s ease;
                    cursor: pointer;
                }
                .modal-door-3d-layer.dimmed {
                    opacity: 0.15;
                }
                .modal-layer-face {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    border-radius: 6px;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    box-shadow: 0px 6px 18px rgba(122, 113, 95, 0.15);
                    transform-style: preserve-3d;
                }
                .modal-door-3d-layer.highlighted .modal-layer-face {
                    border-color: #D4AF37;
                    box-shadow: 0 0 18px rgba(212, 175, 55, 0.35);
                }
                .modal-layer-edge-right,
                .modal-layer-edge-bottom {
                    position: absolute;
                    background: rgba(145, 133, 115, 0.45);
                    transform-style: preserve-3d;
                }
                .modal-layer-edge-right {
                    top: 0;
                    right: -3px;
                    width: 3px;
                    height: 100%;
                    transform: rotateY(90deg);
                    transform-origin: left;
                }
                .modal-layer-edge-bottom {
                    bottom: -3px;
                    left: 0;
                    width: 100%;
                    height: 3px;
                    transform: rotateX(-90deg);
                    transform-origin: top;
                }
                .modal-layer-face-overlay {
                    position: absolute;
                    inset: 6px;
                    border: 1px dashed rgba(122, 113, 95, 0.2);
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    padding: 6px;
                    border-radius: 4px;
                    height: calc(100% - 12px);
                    width: calc(100% - 12px);
                }
                .modal-face-tag {
                    font-family: monospace;
                    font-size: 0.6rem;
                    color: #7a715f;
                    font-weight: 700;
                }
                .modal-face-tag.light-text {
                    color: #fff;
                    text-shadow: 0 1px 3px rgba(0,0,0,0.6);
                }
                .modal-door-3d-handle {
                    position: absolute;
                    top: 50%;
                    right: 12px;
                    width: 5px;
                    height: 30px;
                    background: linear-gradient(135deg, #D4AF37, #8B4513);
                    border: 1px solid rgba(255, 255, 255, 0.4);
                    border-radius: 2px;
                    box-shadow: 1px 1px 4px rgba(0,0,0,0.2);
                }

                /* Mid Controls Overlay in Canvas */
                .modal-3d-mid-controls {
                    padding: 12px 20px;
                    background: #ffffff;
                    border-top: 1px solid #eef0eb;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    flex-shrink: 0;
                }
                .modal-3d-presets {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 6px;
                }
                .modal-preset-btn {
                    background: #fff;
                    border: 1px solid #e0d5c1;
                    color: #5D4037;
                    padding: 8px 6px;
                    font-size: 0.72rem;
                    font-weight: 600;
                    border-radius: 20px;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s ease;
                }
                .modal-preset-btn:hover {
                    background: rgba(212, 175, 55, 0.05);
                    border-color: #D4AF37;
                }
                .modal-preset-btn.active {
                    background: #D4AF37;
                    color: #fff;
                    border-color: #D4AF37;
                    box-shadow: 0 2px 6px rgba(212, 175, 55, 0.25);
                }
                .modal-3d-slider-container {
                    display: flex;
                    flex-direction: column;
                }
                .modal-slider-header {
                    display: flex;
                    justify-content: space-between;
                    font-size: 0.72rem;
                    color: #5D4037;
                    font-weight: 600;
                    margin-bottom: 3px;
                }
                .modal-slider-value {
                    color: #D4AF37;
                    font-weight: 700;
                }
                .modal-3d-slider {
                    width: 100%;
                    accent-color: #D4AF37;
                    height: 5px;
                    background: #e5e5e5;
                    border-radius: 3px;
                    cursor: pointer;
                }

                /* Side-by-side controls layout on tablet & desktop */
                @media (min-width: 600px) {
                    .modal-3d-mid-controls {
                        flex-direction: row;
                        align-items: center;
                        justify-content: space-between;
                        gap: 20px;
                    }
                    .modal-3d-presets {
                        flex: 1.3;
                        margin-bottom: 0;
                    }
                    .modal-3d-slider-container {
                        flex: 1;
                    }
                }

                /* Right Panel (Layer Specs Details) */
                .modal-3d-right-panel {
                    width: 320px;
                    background: #ffffff;
                    border-left: 1px solid #eef0eb;
                    display: flex;
                    flex-direction: column;
                    padding: 24px;
                    overflow-y: auto;
                    flex-shrink: 0;
                }
                .modal-3d-spec-cat {
                    font-size: 0.65rem;
                    color: #D4AF37;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    display: block;
                }
                .modal-3d-spec-name {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: #2a2723;
                    line-height: 1.25;
                    margin: 0 0 12px 0;
                }
                .modal-3d-spec-desc {
                    font-size: 0.78rem;
                    color: #7a715f;
                    line-height: 1.6;
                    margin: 0 0 16px 0;
                }
                .modal-3d-metric-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 10px;
                    margin-bottom: 18px;
                }
                .modal-3d-metric-box {
                    background: #fafaf8;
                    border: 1px solid #eef0eb;
                    border-radius: 8px;
                    padding: 10px 14px;
                }
                .modal-3d-metric-label {
                    font-size: 0.6rem;
                    color: #7a715f;
                    text-transform: uppercase;
                    display: block;
                    margin-bottom: 2px;
                }
                .modal-3d-metric-val {
                    font-size: 0.8rem;
                    font-weight: 700;
                    color: #2a2723;
                }
                .modal-3d-shield-title {
                    font-size: 0.78rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #2a2723;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                }
                .modal-3d-shield-list {
                    list-style: none;
                    padding: 0;
                    margin: 0 0 20px 0;
                }
                .modal-3d-shield-item {
                    font-size: 0.72rem;
                    color: #7a715f;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 6px;
                }
                .modal-3d-check {
                    color: #0d9488;
                    font-weight: bold;
                }
                
                /* Responsive Mobile Layout (Tabs & scrollable layers) */
                .modal-3d-mobile-tabs-container {
                    display: none;
                    background: #ffffff;
                    border-bottom: 1px solid #eef0eb;
                    padding: 12px 16px 10px 16px;
                    flex-direction: column;
                    gap: 8px;
                    flex-shrink: 0;
                }
                .modal-3d-mobile-tabs-label {
                    font-size: 0.72rem;
                    font-weight: 700;
                    color: #7a715f;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 2px;
                }
                .modal-3d-mobile-tabs-scroll {
                    display: flex;
                    overflow-x: auto;
                    white-space: nowrap;
                    gap: 8px;
                    padding-bottom: 4px;
                    -webkit-overflow-scrolling: touch;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .modal-3d-mobile-tabs-scroll::-webkit-scrollbar {
                    display: none;
                }
                .modal-3d-mobile-tab-btn {
                    display: inline-block;
                    padding: 6px 14px;
                    font-size: 0.72rem;
                    font-weight: 600;
                    border-radius: 20px;
                    border: 1px solid #e0d5c1;
                    background: #fff;
                    color: #5D4037;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                .modal-3d-mobile-tab-btn.active {
                    background: #D4AF37;
                    color: #fff;
                    border-color: #D4AF37;
                }

                @media (max-width: 992px) {
                    .modal-3d-dashboard-container {
                        height: auto;
                        overflow: visible;
                    }
                    .modal-3d-header {
                        flex-direction: row;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                        padding: 12px 16px;
                    }
                    .modal-3d-brand {
                        width: auto;
                    }
                    .modal-3d-brand-title {
                        white-space: nowrap;
                    }
                    .modal-3d-header-toggles {
                        margin-right: 40px; /* Leave space for close button */
                    }
                    .modal-3d-left-panel {
                        display: none;
                    }
                    .modal-3d-mobile-tabs-container {
                        display: flex;
                    }
                    .modal-3d-body {
                        flex-direction: column;
                        height: auto;
                        overflow: visible;
                    }
                    .modal-3d-center-panel {
                        flex: none;
                        height: auto;
                        overflow: visible;
                    }
                    .modal-3d-canvas-wrapper {
                        height: 320px;
                        min-height: 320px;
                        padding: 10px;
                    }
                    .modal-3d-mid-controls {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 10px;
                        padding: 12px 16px;
                        border-bottom: 1px solid #eef0eb;
                    }
                    .modal-3d-presets {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 6px;
                    }
                    .modal-preset-btn {
                        padding: 8px 4px;
                        font-size: 0.68rem;
                        text-align: center;
                        white-space: nowrap;
                    }
                    .modal-3d-slider-container {
                        width: 100%;
                    }
                    .modal-slider-header {
                        font-size: 0.72rem;
                    }
                    .modal-3d-right-panel {
                        width: 100%;
                        border-left: none;
                        border-top: none;
                        flex-shrink: 1;
                        overflow-y: visible;
                        padding: 20px;
                    }
                    .modal-3d-metric-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 8px;
                    }
                }

                @media (max-width: 480px) {
                    .modal-3d-metric-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 6px;
                    }
                    .modal-3d-metric-box {
                        padding: 8px 6px;
                        border-radius: 6px;
                    }
                    .modal-3d-metric-label {
                        font-size: 0.52rem;
                    }
                    .modal-3d-metric-val {
                        font-size: 0.68rem;
                    }
                    .modal-3d-header {
                        padding: 12px 16px;
                    }
                    .modal-3d-brand-title {
                        font-size: 0.85rem;
                    }
                    .modal-3d-header-toggle-btn {
                        padding: 4px 8px;
                        font-size: 0.7rem;
                    }
                    .modal-3d-mid-controls {
                        gap: 8px;
                        padding: 10px 12px;
                    }
                    .modal-preset-btn {
                        font-size: 0.62rem;
                        padding: 6px 3px;
                    }
                }
            ` }} />

            {/* 1. HEADER SECTION (with Toggle controls) */}
            <header className="modal-3d-header">
                <div className="modal-3d-brand">
                    <span className="modal-3d-dot" />
                    <h1 className="modal-3d-brand-title">3D View</h1>
                    
                </div>

                {/* Switch between 2D & 3D */}
                <div className="modal-3d-header-toggles">
                    <button
                        onClick={onToggle2D}
                        className="modal-3d-header-toggle-btn secondary"
                    >
                        ← Back
                    </button>
                    <button
                        disabled
                        className="modal-3d-header-toggle-btn primary"
                    >
                        3D View
                    </button>
                </div>
            </header>

            {/* Mobile Horizontal Layer Tabs */}
            <div className="modal-3d-mobile-tabs-container">
                <span className="modal-3d-mobile-tabs-label">Layers</span>
                <div className="modal-3d-mobile-tabs-scroll">
                    {layers.map((layer, index) => {
                        const isActive = activeLayer === index;
                        return (
                            <button
                                key={index}
                                className={`modal-3d-mobile-tab-btn ${isActive ? "active" : ""}`}
                                onClick={() => setActiveLayer(index)}
                            >
                                0{index + 1} {layer.title.split(" ")[0]}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 2. MAIN CONTENT BODY */}
            <div className="modal-3d-body">
                {/* Desktop Left Column: Structural Selector */}
                <div className="modal-3d-left-panel">
                    <h3 className="modal-3d-panel-title">Component Architecture</h3>
                    <p className="modal-3d-panel-desc">Select individual structural layers to inspect engineering details.</p>
                    
                    <div className="modal-3d-layer-list">
                        {layers.map((layer, index) => {
                            const isActive = activeLayer === index;
                            return (
                                <button
                                    key={index}
                                    className={`modal-3d-layer-btn ${isActive ? "active" : ""}`}
                                    onClick={() => setActiveLayer(index)}
                                >
                                    <span className="modal-3d-layer-idx">0{index + 1}</span>
                                    <div className="modal-3d-layer-info">
                                        <h4 className="modal-3d-layer-name">{layer.title}</h4>
                                        <span className="modal-3d-layer-mat">{layer.material}</span>
                                    </div>
                                    {isActive && <div className="modal-3d-layer-glow" />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Center Column: 3D Viewport canvas */}
                <div className="modal-3d-center-panel">
                    <div className="modal-3d-canvas-wrapper" onClick={() => setActiveLayer(0)}>
                        {/* Grid Background */}
                        <div className="modal-3d-grid-bg" />

                        {/* Interactive 3D Model Scene */}
                        <div className="modal-3d-scene">
                            <div className="modal-door-3d-model" style={getPerspectiveStyle()} onClick={(e) => e.stopPropagation()}>
                                {layers.map((layer, index) => {
                                    const midIndex = (layers.length - 1) / 2;
                                    const zOffset = (midIndex - index) * explodeValue;
                                    const isHighlighted = activeLayer === index;
                                    const isAnyActive = activeLayer !== null;

                                    // Dynamic Styling for Top layer (actual image) vs Backing layers
                                    const faceStyle = index === 0 && doorImage
                                        ? {
                                            backgroundImage: `url(${doorImage})`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            backgroundColor: "transparent"
                                          }
                                        : {
                                            background: layer.color
                                          };

                                    return (
                                        <div
                                            key={index}
                                            className={`modal-door-3d-layer ${isHighlighted ? "highlighted" : ""} ${
                                                isAnyActive && !isHighlighted ? "dimmed" : ""
                                            }`}
                                            style={{
                                                transform: `translateZ(${zOffset}px)`,
                                                zIndex: layers.length - index,
                                            }}
                                            onClick={() => setActiveLayer(index)}
                                        >
                                            <div className="modal-layer-face" style={faceStyle}>
                                                <div className="modal-layer-face-overlay">
                                                    <span className={`modal-face-tag ${index === 0 && doorImage ? "light-text" : ""}`}>
                                                        0{index + 1}
                                                    </span>
                                                    {index === 0 && (
                                                        <div className="modal-door-3d-handle" />
                                                    )}
                                                </div>
                                                <div className="modal-layer-edge-right" />
                                                <div className="modal-layer-edge-bottom" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Docked control buttons and sliders */}
                    <div className="modal-3d-mid-controls" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-3d-presets">
                            <button
                                className={`modal-preset-btn ${perspectivePreset === "isometric" ? "active" : ""}`}
                                onClick={() => setPerspectivePreset("isometric")}
                            >
                                Isometric View
                            </button>
                            <button
                                className={`modal-preset-btn ${perspectivePreset === "front" ? "active" : ""}`}
                                onClick={() => setPerspectivePreset("front")}
                            >
                                Front View
                            </button>
                            <button
                                className={`modal-preset-btn ${perspectivePreset === "profile" ? "active" : ""}`}
                                onClick={() => setPerspectivePreset("profile")}
                            >
                                Edge Profile
                            </button>
                        </div>

                        <div className="modal-3d-slider-container">
                            <div className="modal-slider-header">
                                <span>Explode Spacing</span>
                                <span className="modal-slider-value">{explodeValue * 2}mm</span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="90"
                                value={explodeValue}
                                onChange={(e) => setExplodeValue(Number(e.target.value))}
                                className="modal-3d-slider"
                            />
                        </div>
                    </div>
                </div>

                {/* Right Column: Layer Spec sheet details & WhatsApp action button */}
                <div className="modal-3d-right-panel">
                    {layers[activeLayer] ? (
                        <>
                            <span className="modal-3d-spec-cat">Selected Layer Specs</span>
                            <h2 className="modal-3d-spec-name">{layers[activeLayer].title}</h2>
                            <p className="modal-3d-spec-desc">{layers[activeLayer].description}</p>
                            
                            <div className="modal-3d-metric-grid">
                                <div className="modal-3d-metric-box">
                                    <span className="modal-3d-metric-label">Material Composition</span>
                                    <span className="modal-3d-metric-val">{layers[activeLayer].material}</span>
                                </div>
                                <div className="modal-3d-metric-box">
                                    <span className="modal-3d-metric-label">Layer Thickness</span>
                                    <span className="modal-3d-metric-val">{layers[activeLayer].thickness}</span>
                                </div>
                                <div className="modal-3d-metric-box">
                                    <span className="modal-3d-metric-label">Unit Mass Density</span>
                                    <span className="modal-3d-metric-val">{layers[activeLayer].weight}</span>
                                </div>
                            </div>

                            <h4 className="modal-3d-shield-title">Shield Certifications</h4>
                            <ul className="modal-3d-shield-list">
                                <li className="modal-3d-shield-item">
                                    <span className="modal-3d-check">✓</span> Warp Resistant Core
                                </li>
                                <li className="modal-3d-shield-item">
                                    <span className="modal-3d-check">✓</span> High-Impact Force Attenuation
                                </li>
                                <li className="modal-3d-shield-item">
                                    <span className="modal-3d-check">✓</span> ISO Moisture/Termite Isolation
                                </li>
                            </ul>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: "0.8rem", textAlign: "center" }}>
                            Select a structural layer component to view specifications.
                        </div>
                    )}

                    {/* WhatsApp CTA Call-to-action */}
                    <div style={{ marginTop: "auto", paddingTop: "15px" }}>
                        <a
                            href={`https://wa.me/918374771149?text=${encodeURIComponent(
                                `Hi, I would like to get a quote for this door.
                                
Door Type: ${selectedDoor.type}
Door Model: ${selectedDoor.code}

Please share the price and available details.`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                background: colors.gold,
                                color: "#fff",
                                border: "none",
                                borderRadius: "12px",
                                fontWeight: "700",
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "10px",
                                padding: "14px 20px",
                                fontSize: "0.9rem",
                                transition: "all 0.3s ease",
                                boxShadow: "0 6px 15px rgba(212,175,55,0.25)"
                            }}
                        >
                            <i className="fa-brands fa-whatsapp"></i>
                            Get Instant Quote
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
